import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { markNewSignupFlow } from '../../utils/googleAuth';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import SignupStepBadge from '../../components/auth/SignupStepBadge';

const ProfilePhotoSetup: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { isRTL, language } = useLocalization();
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finish = () => {
    markNewSignupFlow(false);
    navigate('/loading', { replace: true });
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(isRTL ? 'الحد الأقصى 5 ميجابايت' : 'Max 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result || ''));
    reader.readAsDataURL(file);
  };

  const onUpload = async () => {
    if (!preview) {
      finish();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const user = await AuthService.updateProfilePhoto(preview);
      setUser(user);
      finish();
    } catch (err: any) {
      setError(err?.message || (isRTL ? 'فشل الرفع' : 'Upload failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      <div className="bg-primary dark:bg-primary-dark py-4 px-4">
        <div className="max-w-md mx-auto flex items-center gap-2">
          <MorafaaLogo size="sm" variant="light" />
          <span className="text-white font-bold">
            {isRTL ? 'صورة الملف الشخصي' : 'Profile photo'}
          </span>
        </div>
      </div>
      <div className="flex justify-center p-6">
        <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 space-y-4 text-center">
          <SignupStepBadge step={3} isRTL={isRTL} />
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {isRTL ? 'رفع صورة الملف الشخصي اختياري' : 'Profile photo upload is optional'}
          </p>
          {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
          {preview ? (
            <img src={preview} alt="preview" className="w-28 h-28 rounded-full object-cover mx-auto" />
          ) : (
            <div className="w-28 h-28 rounded-full bg-slate-100 dark:bg-slate-700 mx-auto" />
          )}
          <input type="file" accept="image/*" onChange={onFile} className="mx-auto block text-sm" />
          <button
            type="button"
            onClick={onUpload}
            disabled={loading}
            className="w-full bg-primary text-white py-3 rounded-lg font-medium disabled:opacity-50"
          >
            {loading
              ? language === 'ar' ? 'جاري الحفظ...' : 'Saving...'
              : preview
                ? language === 'ar' ? 'حفظ ومتابعة' : 'Save & continue'
                : language === 'ar' ? 'تخطي' : 'Skip'}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePhotoSetup;
